/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { jsx } from '@compiled/react';

import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';
import { useRegenerationStatus } from '../../hooks/useRegenerationStatus';
import { useSyncedSelectedSuggestion } from '../../hooks/useSyncedSelectedSuggestion';
import {
	addAcceptSuggestionAnalytics,
	addDiscardSuggestionAnalytics,
	fireSuggestionReasoningViewedAnalytics,
} from '../../utils/analytics';
import { updateSuggestionStatus } from '../../utils/api';
import { applySuggestion } from '../../utils/applySuggestion';
import { getAnchorLocalId } from '../../utils/getAnchorLocalId';
import { AnchoredCard, type AnchoredCardAnchorNames } from '../AnchoredCard';

import { StatusBanner } from './StatusBanner';
import { SuggestionCard } from './SuggestionCard';
import { triggerSuggestedRemix } from './triggerSuggestedRemix';

// After accepting a suggested remix, wait this long before navigating so the remix has a moment to
// render at its anchor while the card is still there.
const SUGGESTED_REMIX_NAVIGATE_DELAY_MS = 2000;

/**
 * We render this in a content component because rendering directly in a Decoration widget
 * will cause it to inherit font stylings from Editor which is not appropriate for this component.
 */
export const SuggestionCardContentComponent = ({
	api,
	onShowSidebar,
	view,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	onShowSidebar?: () => void;
	view?: EditorView;
}): JSX.Element | undefined => {
	const cardRef = useRef<HTMLDivElement>(null);
	const [isHidingDeletedDiffs, setIsHidingDeletedDiffs] = useState(false);

	// Positioning-critical slice — kept synchronous (no debounce) so the CSS-anchor references
	// stay in lock-step with the ProseMirror transaction that swapped the selection.
	const { selectedSuggestion, baseLocalId } = useSyncedSelectedSuggestion(api);

	// Reset the toggle to default (show deletions) whenever the selected suggestion changes
	useEffect(() => {
		setIsHidingDeletedDiffs(false);
	}, [selectedSuggestion?.id]);

	const anchorLocalId = selectedSuggestion ? getAnchorLocalId(selectedSuggestion) : undefined;

	const { orderedSuggestionIds, isHidden, regeneratingSuggestionIds } =
		useSharedPluginStateWithSelector(api, ['aiSuggestions', 'editorViewMode'], (state) => {
			const { orderedSuggestionIds, isHidden, regeneratingSuggestionIds } =
				state?.aiSuggestionsState ?? {};

			return {
				orderedSuggestionIds,
				// View mode is a hard override: never show the suggestion card while read-only.
				isHidden: (isHidden ?? false) || state?.editorViewModeState?.mode === 'view',
				regeneratingSuggestionIds,
			};
		});

	// Checked against the synced `selectedSuggestion.id` (not the debounced plugin
	// state's own `selectedSuggestion`) so this always reflects the suggestion
	// actually being rendered — otherwise a quick navigation away from a
	// regenerating suggestion can transiently attribute its regenerating status to
	// whichever suggestion is selected next.
	const isRegenerating = selectedSuggestion
		? (regeneratingSuggestionIds?.has(selectedSuggestion.id) ?? false)
		: false;

	const regenerationStatus = useRegenerationStatus(selectedSuggestion, isRegenerating);
	const banner = regenerationStatus ? <StatusBanner status={regenerationStatus} /> : undefined;

	const anchorNames: AnchoredCardAnchorNames = useMemo(
		() => ({
			start: `--card-${anchorLocalId}-start`,
			end: `--card-${anchorLocalId}-end`,
			base: `--button-${baseLocalId}`,
		}),
		[anchorLocalId, baseLocalId],
	);

	const onScrollBackClick = useCallback(() => {
		if (!selectedSuggestion) {
			return;
		}
		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id);
	}, [api, selectedSuggestion]);

	const onToggleDeletedDiffs = useCallback(() => {
		if (!selectedSuggestion) {
			return;
		}
		const updatedHideDeletedDiffs = !isHidingDeletedDiffs;
		setIsHidingDeletedDiffs(updatedHideDeletedDiffs);
		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id, {
			hideDeletedDiffs: updatedHideDeletedDiffs,
			scrollIntoView: false,
		});
	}, [api, isHidingDeletedDiffs, selectedSuggestion]);

	if (selectedSuggestion === undefined || isHidden) {
		return;
	}

	const onClose = () => {
		api?.aiSuggestions?.actions.selectSuggestion(null, { interactionPoint: 'card' });
	};

	const onDiscard = () => {
		api?.core?.actions.execute(({ tr }) => {
			updateSuggestionStatus({
				updates: [{ id: selectedSuggestion.id, status: 'DISMISSED' }],
			});

			addDiscardSuggestionAnalytics(api?.analytics?.actions)(tr, selectedSuggestion, 'card');

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});

			api?.showDiff?.commands.hideDiff?.({ tr });

			return tr;
		});

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onAccept = () => {
		updateSuggestionStatus({
			updates: [{ id: selectedSuggestion.id, status: 'ACTIONED' }],
		});

		api?.core?.actions.execute(({ tr }) => {
			addAcceptSuggestionAnalytics(api?.analytics?.actions)(tr, selectedSuggestion, 'card');
			api?.showDiff?.commands.hideDiff?.({ tr });
			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});

		if (selectedSuggestion.actionKind === 'SUGGESTED_REMIX') {
			// Suggested remixes carry no tool calls to apply; hand the backend-supplied action
			// to the remix flow instead. Engine wiring lands in the malleable-ui follow-up.
			const action = selectedSuggestion.action;
			triggerSuggestedRemix({
				api,
				launchOptions: action?.launchOptions,
				localIds: action?.localIds,
				userPrompt: action?.userPrompt,
			});
			// Give the remix a beat to appear at the anchor before the card jumps to the next suggestion.
			setTimeout(() => onNavigate('down'), SUGGESTED_REMIX_NAVIGATE_DELAY_MS);
			return;
		}

		setTimeout(() => {
			applySuggestion({ api, suggestion: selectedSuggestion });
		}, 0);

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onDismiss = () => {
		api?.core?.actions.execute(({ tr }) => {
			addDiscardSuggestionAnalytics(api?.analytics?.actions)(tr, selectedSuggestion, 'card');

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});
		onNavigate('down');
	};

	const onGoodSuggestion = () => {
		api?.aiSuggestions?.actions.submitSuggestionFeedback(selectedSuggestion.id, 'good', {
			interactionPoint: 'card',
		});
	};

	const onBadSuggestion = () => {
		api?.aiSuggestions?.actions.submitSuggestionFeedback(selectedSuggestion.id, 'bad', {
			interactionPoint: 'card',
		});
	};

	const onReasoningViewed = () => {
		fireSuggestionReasoningViewedAnalytics(api?.analytics?.actions, selectedSuggestion, 'card');
	};

	const onNavigate = (direction: 'up' | 'down') => {
		const currentId = selectedSuggestion.id;
		if (!orderedSuggestionIds || orderedSuggestionIds?.length === 0) {
			return undefined;
		}
		const idx = orderedSuggestionIds.indexOf(currentId);
		if (idx === -1) {
			return undefined;
		}
		const delta = direction === 'up' ? -1 : 1;
		const nextIdx = (idx + delta + orderedSuggestionIds.length) % orderedSuggestionIds.length;
		const nextId = orderedSuggestionIds[nextIdx];
		// When only one suggestion exists, the modulo wraps back to the current id.
		// Skip navigation in that case so we don't re-select the suggestion that's about to be removed.
		if (nextId === currentId) {
			return undefined;
		}
		api?.aiSuggestions?.actions.selectSuggestion(nextId);
	};

	return (
		<AnchoredCard
			// Remounts when a different suggestion is opened, which replays the fade-in — the
			// elements are otherwise reused across navigations.
			key={selectedSuggestion.id}
			anchorNames={anchorNames}
			cardRef={cardRef}
			onScrollBack={onScrollBackClick}
			targetId={baseLocalId}
			view={view}
		>
			<SuggestionCard
				ref={cardRef}
				suggestion={selectedSuggestion}
				onNavigate={
					orderedSuggestionIds && orderedSuggestionIds.length > 1 ? onNavigate : undefined
				}
				onClose={onClose}
				onDiscard={onDiscard}
				onDismiss={onDismiss}
				onAccept={onAccept}
				onGoodSuggestion={onGoodSuggestion}
				onBadSuggestion={onBadSuggestion}
				onReasoningViewed={onReasoningViewed}
				onShowSidebar={onShowSidebar}
				onToggleDeletedDiffs={onToggleDeletedDiffs}
				isHidingDeletedDiffs={isHidingDeletedDiffs}
				isRegenerating={isRegenerating}
				banner={banner}
			/>
		</AnchoredCard>
	);
};
